import { html, LitElement, PropertyValues } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { customElement, property, state } from "lit/decorators.js";
import { OrderManagementContext, orderManagementContextKey } from "../order-management.context";
import { repeat } from "lit/directives/repeat.js";
import { BasketDateLine, BasketTimeSlotLine } from "../../cart.types";
import { when } from "lit/directives/when.js";
import { OrderDtoReadable } from "../../api";
import { createRef, ref } from "lit/directives/ref.js";
import { Subscription } from "rxjs";
import { consume } from "@lit/context";


@customElement("order-summary")
export class OrderSummaryElement extends LitElement {
    private _basketLineSubscription: Subscription | undefined;
    private _orderStateSubscription: Subscription | undefined;

    private rootElementRef = createRef();

    @consume({ context: orderManagementContextKey, subscribe: true })

    @state()
    private orderManagementContext? : OrderManagementContext | null = null;

    @state()
    private basketLines : BasketDateLine[] = [];

    @state()
    private orderState: OrderDtoReadable | null = null;

    @state()
    public showContinueButton: boolean = false;

    @state()
    private discountActionLoading: boolean = false;

    @state()
    private discountCode: string = "";

    @state()
    private discountCodeErrorMessage: string | undefined;

    @property({ type: Boolean})
    public checkoutInProgress: boolean = false;


    public override connectedCallback() {
        super.connectedCallback();

        this._basketLineSubscription =this.orderManagementContext?.basketLines$.subscribe({ next: (next) => this.basketLines = next });
        this._orderStateSubscription = this.orderManagementContext?.orderState$.subscribe({ next: (next) => { this.orderState = next; this.showContinueButton = next?.orderLines?.length ?? 0 > 0 ?  true : false; } });

        window.addEventListener("basket-button-clicked", this.handleBasketButtonClicked.bind(this));
        window.addEventListener("hide-modal-requested", this.handleBasketButtonClicked.bind(this));
    }

    public override updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        if(_changedProperties.has("orderManagementContext")) {
            if(this._basketLineSubscription) {
                this._basketLineSubscription.unsubscribe();
            }
            if(this._orderStateSubscription) {
                this._orderStateSubscription.unsubscribe();
            }
            this._basketLineSubscription = this.orderManagementContext?.basketLines$.subscribe({ next: (next) => this.basketLines = next });
            this._orderStateSubscription = this.orderManagementContext?.orderState$.subscribe({ next: (next) => this.orderState = next });
        }
    }

    private removeProduct(basketDetailLine: BasketTimeSlotLine) {
        this.orderManagementContext?.removeProduct(basketDetailLine);
    }

    private handleBasketButtonClicked() {
        this.classList.toggle("hidden");
    }

    private async handleApplyDiscountCode() {
        if(!this.discountCode) {
            this.discountCodeErrorMessage = "Discount code cannot be empty.";
            return;
        }
        this.discountCodeErrorMessage = undefined;
        this.discountActionLoading = true;
        let result = await this.orderManagementContext?.applyDiscountCode(this.discountCode);
        if(result === false) {
            this.discountCode = "";
            this.discountCodeErrorMessage = "Invalid discount code.";
        }
        this.discountActionLoading = false;
    }

    private async handleClearDiscountCode() {
        this.discountActionLoading = true;
        this.discountCodeErrorMessage = undefined;
        await this.orderManagementContext?.clearDiscountCode();
        this.discountActionLoading = false;
    }

    private calculateTotalAdjustment(): number {
        let total = 0;

        for (const basketLine of this.basketLines) {
            for (const detail of basketLine.details) {
                for (const orderLine of detail.orderLines) {
                    total += (orderLine.unitPrice?.adjustment?.withTax ?? 0) * orderLine.quantity;
                    total += orderLine.totalPrice?.adjustment?.withTax ?? 0;
                }
            }
        }

        total += this.orderState?.totalPrice?.adjustment?.withTax ?? 0;

        return -total;
    }

    protected override render() {
        let totalAdjustment = this.calculateTotalAdjustment();
        const defaultInputClass = "peer block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-mcr-purple-600 sm:text-sm/6 aria-invalid:outline-red-500";
        return html`
            <div class="mb-4 text-2xl text-mcr-purple">Basket Details</div>
            ${when(this.basketLines.length == 0,
                () => html`<div>Your cart is empty</div>`,
                () => html`
                    <div class="flex flex-col grow overflow-auto -mx-4 px-4">
                        ${repeat(this.basketLines, basketLine => basketLine.date.toString(), basketLine => html`
                            <div>
                                <div>${basketLine.date.format('dddd, Do MMM')}</div>
                                ${repeat(basketLine.details, basketDetailLine => basketDetailLine.product, basketDetailLine => html`
                                    <div class="flex items-center justify-between pl-4">
                                        <div class="font-bold">${basketDetailLine.date.format('h:mma')} - ${basketDetailLine.product}</div>
                                        <button class="cursor-pointer" @click="${() => this.removeProduct(basketDetailLine)}">
                                            <svg class="size-5 text-red-600">
                                                <use href="#delete-icon" />
                                            </svg>
                                        </button>
                                    </div>
                                    ${repeat(basketDetailLine.orderLines, orderLine => orderLine.id, orderLine => html`
                                        <div class="flex items-center justify-between pl-4">
                                            <div>${orderLine.quantity} x ${orderLine.productVariantName}</div>
                                            <div class="flex items-center gap-2">
                                                ${when(orderLine.totalPrice!.value!.withTax != orderLine.totalPrice!.withoutAdjustments!.withTax,
                                                    () => html`<del class="text-red-600">£${orderLine.totalPrice!.withoutAdjustments!.withTax!.toFixed(2)}</del>`
                                                )}
                                                <div>£${orderLine.totalPrice!.value!.withTax!.toFixed(2)}</div>
                                            </div>
                                        </div>
                                    `)}
                                `)}
                            </div>
                        `)}
                    </div>
                    <div class="mt-2 border-t border-dotted border-neutral-800 pt-2">
                        ${when(this.orderState?.appliedDiscountCodes?.length ?? 0 > 0,
                            () => html`
                                <div>
                                    <div class="font-bold">Discount Code</div>
                                    <div class="flex gap-2">
                                        <div class="grow-1 flex gap-1 flex-wrap items-center">
                                            ${this.orderState?.appliedDiscountCodes?.map(code => html`<div class="bg-neutral-200 flex px-2 py-1 rounded">${code}</div>`)}
                                        </div>
                                            <loading-button buttonText="Clear"
                                                            @click=${() => this.handleClearDiscountCode()}
                                                            ?loading=${this.discountActionLoading}></loading-button>

                                    </div>
                                </div>
                            `,
                            () => html`
                                <div>
                                    <div class="font-bold">Discount Code</div>
                                    <div class="flex gap-2">
                                        <div class="grow-1">
                                            <input type="text" class="${defaultInputClass}"
                                                placeholder="Enter discount code"
                                                @input=${(e: Event & { target: HTMLInputElement }) => this.discountCode = e.target.value}
                                                value=${this.discountCode} />
                                            ${when(this.discountCodeErrorMessage, () => html`<div class="text-red-600">${this.discountCodeErrorMessage}</div>`)}
                                        </div>
                                        <loading-button buttonText="Apply" @click=${() => this.handleApplyDiscountCode()}
                                                        ?loading=${this.discountActionLoading}></loading-button>
                                    </div>
                                </div>
                            `
                        )}
                    </div>
                    <div class="mt-2 border-t border-dotted border-neutral-800 pt-2">
                        <div class="flex items-center justify-between">
                            <div class="font-bold">Booking Fee</div>
                            <div>£0.00</div>
                        </div>
                        <div class="flex items-center justify-between">
                            <div class="font-bold">Total (inc VAT)</div>
                            <div>£${this.orderState?.balanceDue?.toFixed(2)}</div>
                        </div>
                        ${when(totalAdjustment > 0,
                            () => html`
                                <div class="font-bold text-blue-500 text-center">You saved £${totalAdjustment.toFixed(2)}</div>
                            `)}
                        ${when(
                            this.showContinueButton || !this.checkoutInProgress,
                            () => html`<a href="/checkout" class="mt-4 block w-full cursor-pointer rounded px-4 py-2 text-center transition bg-mcr-purple px-4 py-2 text-white hover:bg-mcr-purple-700">Proceed to Checkout</a>`,
                            () => html`<a href="/" class="mt-4 block w-full cursor-pointer rounded outline-2 transition text-mcr-purple-800 bg-mcr-purple-100 outline-mcr-purple-500 px-4 py-2 text-center hover:text-white hover:bg-mcr-purple-300 hover:outline-mcr-purple-600">Go back</a>`
                        )}
                    </div>
                `
            )}
        `;
    }

    protected override createRenderRoot() {
        return this;
    }
}